import { formatDate } from "utils/formatDate";
import { DATE_FORMATS } from "../../constants";
import { RootState } from "../../reducers";
import { ImageFromVolume, Volume, VolumeLimits, VolumeType } from "./types";
import { createSelector } from "reselect";

const getVolumes = (state: RootState) => state.storage.volumes;
export const tableVolumesSelector = createSelector([getVolumes], (volumes) => {
  return volumes
    ? volumes.map((volume) => ({
        name: volume.name,
        id: volume.id,
        status: volume.status,
        size: volume.size,
        sizeString: `${volume.size} GiB`,
        createdAt: formatDate(
          new Date(volume.created_at),
          DATE_FORMATS.DATETIME
        ),
        bootable: volume.bootable,
        serverId: volume.attachments[0]?.server_id,
        detailedName: `${volume.name} (status: ${volume.status})`,
        description: volume.description
      }))
    : null;
});

// export const tableVolumesSelector = (
//   state: RootState
// ): TableVolume[] | null =>
//   state.storage.volumes?.map((volume) => ({
//     name: volume.name,
//     id: volume.id,
//     status: volume.status,
//     size: volume.size,
//     sizeString: `${volume.size} GiB`,
//     createdAt: formatDate(new Date(volume.created_at), DATE_FORMATS.DATETIME),
//     bootable: volume.bootable,
//     serverId: volume.attachments[0]?.server_id,
//     detailedName: `${volume.name} (status: ${volume.status})`,
//     description: volume.description
//   })) || null;

const getSnapshots = (state: RootState) => state.storage.snapshots;
export const tableSnapshotsSelector = createSelector(
  [getSnapshots],
  (snapshots) => {
    return snapshots
      ? snapshots.map((snapshot) => ({
          id: snapshot.id,
          name: snapshot.name,
          status: snapshot.status,
          size: `${snapshot.size} GiB`,
          createdAt: formatDate(
            new Date(snapshot.created_at),
            DATE_FORMATS.DATETIME
          ),
          description: snapshot.description
        }))
      : null;
  }
);

// export const tableSnapshotsSelector = (
//   state: RootState
// ): TableSnapshot[] | null =>
//   state.storage.snapshots?.map((snapshot) => ({
//     id: snapshot.id,
//     name: snapshot.name,
//     status: snapshot.status,
//     size: `${snapshot.size} GiB`,
//     createdAt: formatDate(new Date(snapshot.created_at), DATE_FORMATS.DATETIME),
//     description: snapshot.description
//   })) || null;

const getCurrentInstanceId = (state: RootState) => state.instances.instance?.id;
export const tableVirtualMachineVolumesSelector = createSelector(
  [getVolumes, getCurrentInstanceId],
  (volumes, currentInstanceId) => {
    return (
      volumes
        ?.filter((volume) =>
          volume.attachments.some(
            (attachment) => attachment.server_id === currentInstanceId
          )
        )
        .map((volume) => ({
          name: volume.name,
          id: volume.id,
          status: volume.status,
          size: volume.size,
          sizeString: `${volume.size} GiB`,
          createdAt: formatDate(
            new Date(volume.created_at),
            DATE_FORMATS.DATETIME
          ),
          bootable: volume.bootable,
          detailedName: `${volume.name} (status: ${volume.status})`,
          description: volume.description
        })) || null
    );
  }
);

// export const tableVirtualMachineVolumesSelector = (
//   state: RootState
// ): TableVolume[] | null =>
//   state.storage.volumes
//     ?.filter((volume) =>
//       volume.attachments.some(
//         (attachment) => attachment.server_id === state.instances.instance?.id
//       )
//     )
//     .map((volume) => ({
//       name: volume.name,
//       id: volume.id,
//       status: volume.status,
//       size: volume.size,
//       sizeString: `${volume.size} GiB`,
//       createdAt: formatDate(new Date(volume.created_at), DATE_FORMATS.DATETIME),
//       bootable: volume.bootable,
//       detailedName: `${volume.name} (status: ${volume.status})`,
//       description: volume.description
//     })) || null;

export const tableVirtualMachineSnapshotsSelector = createSelector(
  [getSnapshots, getVolumes, getCurrentInstanceId],
  (snapshots, volumes, currentInstanceId) => {
    const virtualMachineVolumeIds = volumes
      ?.filter((volume) =>
        volume.attachments.some(
          (attachment) => attachment.server_id === currentInstanceId
        )
      )
      .map((volume) => volume.id);

    return (
      snapshots
        ?.filter((snapshot) =>
          virtualMachineVolumeIds?.includes(snapshot.volume_id)
        )
        .map((snapshot) => ({
          id: snapshot.id,
          name: snapshot.name,
          status: snapshot.status,
          size: `${snapshot.size} GiB`,
          createdAt: formatDate(
            new Date(snapshot.created_at),
            DATE_FORMATS.DATETIME
          ),
          description: snapshot.description
        })) || null
    );
  }
);
// export const tableVirtualMachineSnapshotsSelector = (
//   state: RootState
// ): TableSnapshot[] | null => {
//   const virtualMachineVolumeIds = state.storage.volumes
//     ?.filter((volume) =>
//       volume.attachments.some(
//         (attachment) => attachment.server_id === state.instances.instance?.id
//       )
//     )
//     .map((volume) => volume.id);

//   return (
//     state.storage.snapshots
//       ?.filter(
//         (snapshot) => virtualMachineVolumeIds?.includes(snapshot.volume_id)
//       )
//       .map((snapshot) => ({
//         id: snapshot.id,
//         name: snapshot.name,
//         status: snapshot.status,
//         size: `${snapshot.size} GiB`,
//         createdAt: formatDate(
//           new Date(snapshot.created_at),
//           DATE_FORMATS.DATETIME
//         ),
//         description: snapshot.description
//       })) || null
//   );
// };

export const isVolumeLoadingSelector = (state: RootState): boolean =>
  state.storage.isVolumeLoading;

export const isVolumeCreatingSelector = (state: RootState): boolean =>
  state.storage.isVolumeCreating;

export const isVolumeUpdatingSelector = (state: RootState): boolean =>
  state.storage.isVolumeUpdating;

export const isVolumeExtendingSelector = (state: RootState): boolean =>
  state.storage.isVolumeExtending;

export const isVolumeDeletingSelector = (state: RootState): boolean =>
  state.storage.isVolumeDeleting;

export const areVolumesDeletingSelector = (state: RootState): boolean =>
  state.storage.areVolumesDeleting;

export const volumeLimitsSelector = (state: RootState): VolumeLimits | null =>
  state.storage.volumeLimits;

export const areVolumeLimitsLoadingSelector = (state: RootState): boolean =>
  state.storage.areVolumeLimitsLoading;

export const isSnapshotLoadingSelector = (state: RootState): boolean =>
  state.storage.isSnapshotLoading;

export const isSnapshotCreatingSelector = (state: RootState): boolean =>
  state.storage.isSnapshotCreating;

export const isSnapshotUpdatingSelector = (state: RootState): boolean =>
  state.storage.isSnapshotUpdating;

export const isSnapshotDeletingSelector = (state: RootState): boolean =>
  state.storage.isSnapshotDeleting;

export const volumeTypesSelector = (state: RootState): VolumeType[] | null =>
  state.storage.volumeTypes;

export const areVolumesLoadingSelector = (state: RootState): boolean =>
  state.storage.areVolumesLoading;

export const areSnapshotsLoadingSelector = (state: RootState): boolean =>
  state.storage.areSnapshotsLoading;

export const areVolumeTypesLoadingSelector = (state: RootState): boolean =>
  state.storage.areVolumeTypesLoading;

export const volumeSelector = (state: RootState): Volume | null =>
  state.storage.volume;

export const imageFromVolumeSelector = (
  state: RootState
): ImageFromVolume | undefined => state.storage.volume?.volume_image_metadata;
